import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';

import { useGetAccountAssets } from '~/hooks/apis/offers/useGetAccountAssets';
import { useGetAccountOpportunities, useGetOffer, useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { formatDateISO } from '~/services/TimeService';

import {
  useBlackoutsContext,
  useEgsDraftsContext,
  useInclusionDraftsContext,
  usePackageOptionsContext,
  usePropertySetupContext,
  useSurchargeGridsContext,
} from '../PricingToolSessionContext';
import { reducePreviousBlackouts } from '../tabs/blackouts/previousBlackouts';
import { egsRooms } from '../tabs/egs/egsModel';
import {
  type BedChoice,
  chosenEgsBeds,
  chosenEgsRooms,
  everyOtherBedDescribed,
  previousEgsRows,
  reducePreviousEgs,
  withEgsChoices,
} from '../tabs/egs/previousEgs';
import { reducePreviousInclusions } from '../tabs/inclusions/previousInclusions';
import { useRecentAssetSfids } from '../tabs/inclusions/useRecentAssetSfids';
import { DEFAULT_OPTION_STATE, collectRoomAssets } from '../tabs/packageOptions/packageOptionsModel';
import {
  previousLengthsOfStay,
  reducePreviousDealOptions,
  replaceWithPreviousPackages,
} from '../tabs/packageOptions/previousDealOptions';
import {
  type PackageRoleChoice,
  type PackageRoleChoices,
  groupPreviousDealOptions,
  hasPackageVariants,
  packageVariantErrors,
  prefillPackageRoles,
} from '../tabs/packageOptions/previousPackageVariants';
import { deriveOpportunityPrefill } from '../tabs/propertySetup/propertySetupModel';
import {
  type SurchargeDates,
  chosenSurchargeDates,
  countOverlappingNotAccumulating,
  isSurchargeDatesDone,
  reducePreviousSurcharges,
  withChosenSurchargeDates,
} from '../tabs/surcharges/previousSurcharges';

import CopyEgsStep from './CopyEgsStep';
import CopyPackageVariantsStep from './CopyPackageVariantsStep';
import CopySectionOption from './CopySectionOption';
import CopySurchargeDatesStep from './CopySurchargeDatesStep';
import {
  COPY_SECTIONS,
  COPY_STEP_LABELS,
  CopySection,
  CopySectionDetails,
  CopyStep,
  blackoutsDetails,
  canCopy,
  copySteps,
  dealOptionsNotes,
  dealOptionsSummary,
  egsNotes,
  egsSummary,
  inclusionsNotes,
  inclusionsSummary,
  isStepComplete,
  previousOpportunityChoices,
  replaceWarning,
  sourceOpportunityLabel,
  surchargesEmptyDetails,
  surchargesNotes,
  surchargesOverlapWarning,
  surchargesSummary,
  toggleCopySection,
} from './copyFromPreviousModel';

type CopyFromPreviousOpportunityDialogProps = {
  offerId: string;
  onClose: () => void;
  open: boolean;
};

export default function CopyFromPreviousOpportunityDialog({
  offerId,
  onClose,
  open,
}: CopyFromPreviousOpportunityDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { optionState, optionStates, replaceFirstOption } = usePackageOptionsContext();
  const { replaceOptionDrafts, linkAssetByName } = useInclusionDraftsContext();
  const { replaceDrafts } = useEgsDraftsContext();
  const { replacePeriods } = useSurchargeGridsContext();
  const { replaceBlackouts } = useBlackoutsContext();
  const [step, setStep] = useState<CopyStep>('opportunity');
  const [sourceOpportunityId, setSourceOpportunityId] = useState('');
  const [selectedSections, setSelectedSections] = useState<Set<CopySection>>(new Set());
  const [pickedSurchargeDates, setPickedSurchargeDates] = useState<Record<string, Partial<SurchargeDates>>>({});
  const [pickedEgsRooms, setPickedEgsRooms] = useState<Record<string, Array<string>>>({});
  const [pickedEgsBeds, setPickedEgsBeds] = useState<Record<string, BedChoice>>({});
  const [pickedEgsOtherBeds, setPickedEgsOtherBeds] = useState<Record<string, string>>({});
  const [skippedEgsRows, setSkippedEgsRows] = useState<ReadonlySet<string>>(new Set());
  const [pickedPackageRoles, setPickedPackageRoles] = useState<PackageRoleChoices>({});

  const { data: offer } = useGetOffer(offerId);
  const { data: accountAssets } = useGetAccountAssets(offer?.vendor_account_id, 'services');
  const {
    data: opportunity,
    error: opportunityError,
    isFetching: isFetchingOpportunity,
  } = useGetOpportunity<App.OpportunityPage>(offerId, { enabled: open });
  const accountId = offer?.vendor_account_id;
  const {
    data: opportunities,
    error: opportunitiesError,
    isLoading: isLoadingOpportunities,
  } = useGetAccountOpportunities(accountId, { enabled: open });

  const choices = useMemo(() => previousOpportunityChoices(opportunities ?? [], offerId), [opportunities, offerId]);
  const source = choices.find((choice) => choice.id === sourceOpportunityId);
  const sourceName = sourceOpportunityLabel(source);
  const copiesDealOptions = selectedSections.has('dealOptions');
  const copiesInclusions = selectedSections.has('inclusions');
  const copiesSurcharges = selectedSections.has('surcharges');
  const copiesEgs = selectedSections.has('extraGuestSurcharges');
  const copiesBlackouts = selectedSections.has('blackouts');
  const readsRecords = copiesDealOptions || copiesSurcharges || copiesEgs;
  const readsTravelDates = copiesSurcharges || copiesBlackouts;
  const today = formatDateISO();

  const {
    data: related,
    error: relatedError,
    isFetching: isFetchingRelated,
  } = useGetOpportunityRelated(sourceOpportunityId, { enabled: open && !!sourceOpportunityId && readsRecords });
  const { error: targetRelatedError, isFetching: isFetchingTarget } = useGetOpportunityRelated(offerId, {
    enabled: open && readsRecords,
  });
  const {
    data: sourceOpportunity,
    error: sourceOpportunityError,
    isFetching: isFetchingSourceOpportunity,
  } = useGetOpportunity<App.OpportunityPage>(sourceOpportunityId, {
    enabled: open && !!sourceOpportunityId && copiesBlackouts,
  });

  const {
    fx: { currency: netCurrency },
  } = usePropertySetupContext();
  const dealOptionGroups = useMemo(
    () => (related ? groupPreviousDealOptions(related.deals, related.bundled_experiences) : []),
    [related],
  );
  const prefilledPackageRoles = useMemo(() => prefillPackageRoles(dealOptionGroups), [dealOptionGroups]);
  const packageRoles = useMemo(
    () => ({ ...prefilledPackageRoles, ...pickedPackageRoles }),
    [prefilledPackageRoles, pickedPackageRoles],
  );
  const variantErrors = useMemo(
    () => packageVariantErrors(dealOptionGroups, packageRoles),
    [dealOptionGroups, packageRoles],
  );
  const previous = useMemo(
    () => (related && netCurrency ? reducePreviousDealOptions(dealOptionGroups, packageRoles, netCurrency) : undefined),
    [related, netCurrency, dealOptionGroups, packageRoles],
  );
  const previousPackageCount = previous?.packages.length ?? 0;
  const copiedDealOptions = copiesDealOptions && previousPackageCount > 0 ? previous : undefined;
  // Built once: rooms new to Option 1 get fresh ids, and the copied inclusions must point at these ones.
  const copiedFirstOption = useMemo(
    () =>
      copiedDealOptions
        ? replaceWithPreviousPackages(optionStates[0] ?? DEFAULT_OPTION_STATE, copiedDealOptions)
        : undefined,
    [copiedDealOptions, optionStates],
  );
  const { usedSfids: recentAssetSfids, isLoading: isLoadingRecentAssets } = useRecentAssetSfids(accountId, offerId, {
    enabled: open && copiesInclusions,
  });
  const previousInclusions = useMemo(
    () =>
      related && copiedDealOptions && copiedFirstOption
        ? reducePreviousInclusions(
            related.inclusions,
            copiedDealOptions.packages,
            copiedFirstOption,
            netCurrency,
            recentAssetSfids,
          )
        : undefined,
    [related, copiedDealOptions, copiedFirstOption, netCurrency, recentAssetSfids],
  );
  const previousSurcharges = useMemo(
    () =>
      related && opportunity
        ? reducePreviousSurcharges(related.surcharge_periods, netCurrency, deriveOpportunityPrefill(opportunity), today)
        : undefined,
    [related, netCurrency, opportunity, today],
  );
  const surchargeDatesByKey = useMemo(
    () => chosenSurchargeDates(previousSurcharges?.toDate ?? [], pickedSurchargeDates),
    [previousSurcharges, pickedSurchargeDates],
  );
  const chosenSurcharges = useMemo(
    () => (previousSurcharges ? withChosenSurchargeDates(previousSurcharges, surchargeDatesByKey) : []),
    [previousSurcharges, surchargeDatesByKey],
  );
  const surchargePeriodCount = (previousSurcharges?.periods.length ?? 0) + (previousSurcharges?.toDate.length ?? 0);
  const surchargesToCopy = chosenSurcharges.length;
  const surchargesSkipped = Object.values(surchargeDatesByKey).filter(({ skipped }) => skipped).length;
  const surchargeOverlapWarning = copiesSurcharges
    ? surchargesOverlapWarning(countOverlappingNotAccumulating(chosenSurcharges))
    : undefined;
  const previousEgs = useMemo(
    () =>
      related && netCurrency
        ? reducePreviousEgs(
            related.extra_guest_surcharges,
            netCurrency,
            egsRooms((copiedFirstOption ?? optionState).roomsByCategory),
          )
        : undefined,
    [related, netCurrency, optionState, copiedFirstOption],
  );
  const previousEgsCount = previousEgs?.records.length ?? 0;
  const egsRows = useMemo(() => (previousEgs ? previousEgsRows(previousEgs) : []), [previousEgs]);
  const egsRoomsByNameKey = useMemo(
    () => chosenEgsRooms(egsRows, pickedEgsRooms, previousEgs?.rooms.map((room) => room.id) ?? []),
    [egsRows, pickedEgsRooms, previousEgs],
  );
  const egsBedByNameKey = useMemo(() => chosenEgsBeds(egsRows, pickedEgsBeds), [egsRows, pickedEgsBeds]);
  const copiedEgs = useMemo(
    () =>
      previousEgs
        ? withEgsChoices(previousEgs, {
            roomsByNameKey: egsRoomsByNameKey,
            bedByNameKey: egsBedByNameKey,
            otherBedByNameKey: pickedEgsOtherBeds,
            skippedNameKeys: skippedEgsRows,
          })
        : [],
    [previousEgs, egsRoomsByNameKey, egsBedByNameKey, pickedEgsOtherBeds, skippedEgsRows],
  );
  const previousBlackouts = useMemo(
    () =>
      sourceOpportunity && opportunity
        ? reducePreviousBlackouts(sourceOpportunity.black_out_dates, deriveOpportunityPrefill(opportunity), today)
        : undefined,
    [sourceOpportunity, opportunity, today],
  );
  const previousLos = useMemo(() => (previous ? previousLengthsOfStay(previous) : []), [previous]);
  const sectionDetails = useMemo(() => {
    const details: Partial<Record<CopySection, CopySectionDetails>> = {};
    if (previous && netCurrency) {
      details.dealOptions = previous.packages.length
        ? { summary: dealOptionsSummary(previous, previousLos), notes: dealOptionsNotes(previous, netCurrency) }
        : { emptyMessage: `${sourceName} has no Deal Options that can be copied.`, notes: [] };
    }
    if (previousInclusions) {
      details.inclusions = previousInclusions.drafts.length
        ? { summary: inclusionsSummary(previousInclusions), notes: inclusionsNotes(previousInclusions) }
        : {
            emptyMessage: `${sourceName} has no inclusions on the Deal Options being copied.`,
            notes: inclusionsNotes(previousInclusions),
          };
    }
    if (previousSurcharges) {
      details.surcharges = surchargePeriodCount
        ? { summary: surchargesSummary(surchargePeriodCount), notes: surchargesNotes(previousSurcharges) }
        : surchargesEmptyDetails(previousSurcharges, sourceName);
    }
    if (previousEgs && netCurrency) {
      details.extraGuestSurcharges = previousEgs.records.length
        ? { summary: egsSummary(previousEgs.records.length), notes: egsNotes(previousEgs, netCurrency) }
        : { emptyMessage: `${sourceName} has no extra guest surcharges that can be copied.`, notes: [] };
    }
    if (previousBlackouts) {
      details.blackouts = blackoutsDetails(previousBlackouts, sourceName);
    }
    return details;
  }, [
    previous,
    previousLos,
    previousInclusions,
    previousSurcharges,
    surchargePeriodCount,
    previousEgs,
    netCurrency,
    previousBlackouts,
    sourceName,
  ]);

  const readiness = {
    hasSource: !!source,
    selected: selectedSections,
    isFetching:
      isFetchingRelated ||
      isFetchingTarget ||
      (copiesInclusions && isLoadingRecentAssets) ||
      (readsTravelDates && isFetchingOpportunity) ||
      (copiesBlackouts && isFetchingSourceOpportunity),
    dealOptionPackages: previousPackageCount,
    hasPackageVariants: hasPackageVariants(dealOptionGroups),
    packageVariantErrors: variantErrors.length,
    inclusions: previousInclusions?.drafts.length ?? 0,
    surchargePeriods: surchargePeriodCount,
    surchargesToDate: previousSurcharges?.toDate.length ?? 0,
    surchargesToCopy,
    everySurchargeDatedOrSkipped: Object.values(surchargeDatesByKey).every(isSurchargeDatesDone),
    egsRecords: previousEgsCount,
    egsToCopy: copiedEgs.length,
    everyEgsHasRoomsOrSkipped: egsRows.every(
      (row) => skippedEgsRows.has(row.key) || egsRoomsByNameKey[row.key].length > 0,
    ),
    everyEgsOtherBedDescribed: everyOtherBedDescribed(egsRows, {
      bedByNameKey: egsBedByNameKey,
      otherBedByNameKey: pickedEgsOtherBeds,
      skippedNameKeys: skippedEgsRows,
    }),
    blackouts: previousBlackouts?.blackouts.length ?? 0,
  };
  const confirmSummaries: Record<CopySection, string | undefined> = {
    dealOptions: sectionDetails.dealOptions?.summary,
    inclusions: sectionDetails.inclusions?.summary,
    surcharges: surchargesSummary(surchargesToCopy, surchargesSkipped),
    extraGuestSurcharges: egsSummary(
      copiedEgs.length,
      previousEgsCount - copiedEgs.length,
      copiedEgs.filter((record) => !record.beddingType).length,
    ),
    blackouts: sectionDetails.blackouts?.summary,
  };
  const steps = copySteps(readiness);
  const stepIndex = steps.indexOf(step);
  const warning = replaceWarning(selectedSections);

  const handleSurchargeDatesChange = useCallback((key: string, dates: Partial<SurchargeDates>) => {
    setPickedSurchargeDates((current) => ({ ...current, [key]: { ...current[key], ...dates } }));
  }, []);

  const handlePackageRoleChange = useCallback((key: string, choice: PackageRoleChoice) => {
    setPickedPackageRoles((current) => ({ ...current, [key]: choice }));
  }, []);

  const handleEgsRoomsChange = useCallback((key: string, rooms: Array<string>) => {
    setPickedEgsRooms((current) => ({ ...current, [key]: rooms }));
  }, []);

  const handleEgsBedChange = useCallback((key: string, bed: BedChoice) => {
    setPickedEgsBeds((current) => ({ ...current, [key]: bed }));
  }, []);

  const handleEgsOtherBedChange = useCallback((key: string, description: string) => {
    setPickedEgsOtherBeds((current) => ({ ...current, [key]: description }));
  }, []);

  const handleEgsSkipChange = useCallback((key: string, skipped: boolean) => {
    setSkippedEgsRows((current) => {
      const next = new Set(current);
      if (skipped) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);

  function handleSourceChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSourceOpportunityId(event.target.value);
    setPickedPackageRoles({});
    setPickedSurchargeDates({});
    setPickedEgsRooms({});
    setPickedEgsBeds({});
    setPickedEgsOtherBeds({});
    setSkippedEgsRows(new Set());
  }

  function handleSectionChange(event: React.ChangeEvent<HTMLInputElement>) {
    const section = COPY_SECTIONS.find(({ key }) => key === event.target.name)?.key;
    const { checked } = event.target;
    if (!section) {
      return;
    }
    setSelectedSections((current) => toggleCopySection(current, section, checked));
  }

  function handleCopy() {
    if (!source) {
      return;
    }
    if (copiedFirstOption) {
      replaceFirstOption(copiedFirstOption);
    }
    if (copiesInclusions && previousInclusions) {
      // See docs/business-rules.md#inclusion-assets - an unlinked inclusion gets an asset named after it.
      replaceOptionDrafts(
        0,
        previousInclusions.drafts.map((draft) =>
          draft.assetLink
            ? draft
            : { ...draft, assetLink: linkAssetByName(draft.name, collectRoomAssets(accountAssets ?? [])) },
        ),
      );
    }
    if (copiesSurcharges && previousSurcharges) {
      replacePeriods(chosenSurcharges.map(({ period }) => period));
    }
    if (copiesEgs && previousEgs) {
      replaceDrafts(copiedEgs);
    }
    if (copiesBlackouts && previousBlackouts) {
      replaceBlackouts(previousBlackouts.blackouts);
    }
    enqueueSnackbar(`Copied from ${source.name}`, { variant: 'success' });
    onClose();
  }

  function handleExited() {
    setStep('opportunity');
    setSourceOpportunityId('');
    setSelectedSections(new Set());
    setPickedPackageRoles({});
    setPickedSurchargeDates({});
    setPickedEgsRooms({});
    setPickedEgsBeds({});
    setPickedEgsOtherBeds({});
    setSkippedEgsRows(new Set());
  }

  return (
    <Dialog
      fullWidth
      maxWidth={step === 'packageVariants' || step === 'egs' ? 'lg' : 'md'}
      onClose={onClose}
      open={open}
      slotProps={{ transition: { onExited: handleExited } }}
    >
      <DialogTitle>Copy from previous Opportunity</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={stepIndex} sx={{ mb: 3 }}>
          {steps.map((copyStep) => (
            <Step key={copyStep}>
              <StepLabel>{COPY_STEP_LABELS[copyStep]}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 'opportunity' && (
          <>
            <Typography color="text.secondary" variant="body2">
              Pick a previous Opportunity for this property. Nothing is changed in Salesforce.
            </Typography>

            {isLoadingOpportunities && <CircularProgress size={24} sx={{ mt: 2 }} />}

            {opportunitiesError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Unable to load the previous Opportunities for this property.
              </Alert>
            )}

            {opportunities && choices.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This property has no other Opportunities.
              </Alert>
            )}

            {choices.length > 0 && (
              <RadioGroup onChange={handleSourceChange} sx={{ mt: 2 }} value={sourceOpportunityId}>
                {choices.map((choice) => (
                  <FormControlLabel
                    control={<Radio size="small" />}
                    key={choice.id}
                    label={
                      <Stack>
                        <Typography variant="body2">{choice.name}</Typography>
                        <Typography color="text.secondary" variant="caption">
                          {[choice.stage, choice.closeDate && `Close date ${choice.closeDate}`]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      </Stack>
                    }
                    value={choice.id}
                  />
                ))}
              </RadioGroup>
            )}
          </>
        )}

        {step === 'sections' && (
          <>
            <Typography color="text.secondary" variant="body2">
              Pick what to copy from {source?.name}.
            </Typography>

            <FormGroup sx={{ mt: 2 }}>
              {COPY_SECTIONS.map((section) => (
                <CopySectionOption
                  checked={selectedSections.has(section.key)}
                  details={isFetchingRelated ? undefined : sectionDetails[section.key]}
                  disabledReason={
                    section.requires && !selectedSections.has(section.requires.section)
                      ? section.requires.reason
                      : undefined
                  }
                  key={section.key}
                  label={section.label}
                  name={section.key}
                  onChange={handleSectionChange}
                />
              ))}
            </FormGroup>

            {readsRecords && isFetchingRelated && <CircularProgress size={24} sx={{ mt: 2 }} />}

            {readsRecords && !isFetchingRelated && relatedError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Unable to load that Opportunity from Salesforce.
              </Alert>
            )}

            {readsTravelDates && !isFetchingOpportunity && !opportunity && opportunityError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Unable to load this Opportunity&apos;s travel dates, so surcharge periods and blackouts can&apos;t be
                copied.
              </Alert>
            )}

            {copiesBlackouts && !isFetchingSourceOpportunity && sourceOpportunityError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Unable to load that Opportunity&apos;s blackouts from Salesforce.
              </Alert>
            )}

            {readsRecords && !isFetchingTarget && targetRelatedError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Unable to load this Opportunity from Salesforce, so nothing can be copied.
              </Alert>
            )}
          </>
        )}

        {step === 'packageVariants' && (
          <CopyPackageVariantsStep
            choices={packageRoles}
            errors={variantErrors}
            groups={dealOptionGroups}
            onChange={handlePackageRoleChange}
          />
        )}

        {step === 'surchargeDates' && previousSurcharges && (
          <CopySurchargeDatesStep
            datesByKey={surchargeDatesByKey}
            nothingToCopy={surchargesToCopy === 0}
            onDatesChange={handleSurchargeDatesChange}
            toDate={previousSurcharges.toDate}
          />
        )}

        {step === 'egs' && previousEgs && (
          <CopyEgsStep
            bedByNameKey={egsBedByNameKey}
            nothingToCopy={copiedEgs.length === 0}
            onBedChange={handleEgsBedChange}
            onOtherBedChange={handleEgsOtherBedChange}
            onRoomsChange={handleEgsRoomsChange}
            onSkipChange={handleEgsSkipChange}
            otherBedByNameKey={pickedEgsOtherBeds}
            rooms={previousEgs.rooms}
            rows={egsRows}
            roomsByNameKey={egsRoomsByNameKey}
            skippedRows={skippedEgsRows}
          />
        )}

        {step === 'confirm' && (
          <>
            <Typography variant="body2">From {source?.name}:</Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {COPY_SECTIONS.filter((section) => selectedSections.has(section.key)).map((section) => (
                <Typography component="li" key={section.key} variant="body2">
                  {`${section.label}: ${confirmSummaries[section.key]}`}
                </Typography>
              ))}
            </Box>

            {surchargeOverlapWarning && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {surchargeOverlapWarning}
              </Alert>
            )}

            {warning && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {warning}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        {stepIndex > 0 && (
          <Button onClick={() => setStep(steps[stepIndex - 1])} variant="text">
            Back
          </Button>
        )}
        {step === 'confirm' ? (
          <Button disabled={!canCopy(readiness)} onClick={handleCopy} variant="contained">
            Copy
          </Button>
        ) : (
          <Button
            disabled={!isStepComplete(step, readiness)}
            onClick={() => setStep(steps[stepIndex + 1])}
            variant="contained"
          >
            Continue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
